import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { buildClassName } from '@bluebeam/build-class-name';
import { useDataAttributes } from '@bluebeam/react-hooks';
import { useTranslator } from '@bluebeam/react-i18n';
import { GeneralIcons } from '@bluebeam/react-skyline-art';
import { IconButton } from '@bluebeam/react-skyline-icon-button';
import { AnimatePresence, motion } from 'framer-motion';
import { cloneElement, useRef, useState } from 'react';
import { useFocusWithin, useHover, useId, usePress } from 'react-aria';
const directionToTailwindMap = {
    down: 'rotate-[360deg]',
    right: 'rotate-[270deg]',
};
const ToggleSection = ({ actionMenu, actionMenuConfig, asideContent, children, className, isExpandedInitially, isSelected, title, ariaToggle, ...rest }) => {
    const dataAttributes = useDataAttributes(rest);
    const [isExpanded, setIsExpanded] = useState(isExpandedInitially);
    const [isSectionFocused, setIsSectionFocused] = useState(false);
    const toggleButtonRef = useRef();
    const chevronDirection = isExpanded ? 'down' : 'right';
    const uniqueId = useId();
    const { translate } = useTranslator(['local']);
    const { hoverProps, isHovered } = useHover({});
    const { focusWithinProps } = useFocusWithin({
        onFocusWithin: () => setIsSectionFocused(true),
        onBlurWithin: () => setIsSectionFocused(false),
    });
    const toggleSection = (e) => {
        // prevent toggle when user clicks on a clickable child element
        if (e.currentTarget != e.target)
            return;
        setIsExpanded(!isExpanded);
    };
    const { isPressed, pressProps } = usePress({
        onPress: () => setIsExpanded(!isExpanded),
    });
    return (_jsxs(_Fragment, { children: [_jsxs("div", { "aria-expanded": isExpanded ? 'true' : 'false', "aria-controls": uniqueId, className: buildClassName('relative box-border flex h-12 max-w-full cursor-pointer items-center gap-2 border-2 border-solid border-transparent bg-white py-2.5 pl-3.5 pr-[22px]', 'active:bg-primary-blue/30', {
                    'border-primary-blue bg-primary-blue/30': isSelected || isPressed,
                    'border-primary-blue': isSectionFocused,
                    'bg-primary-blue/10': isHovered && !isSelected,
                }, className), onClick: toggleSection, ...hoverProps, ...focusWithinProps, ...dataAttributes, children: [_jsx("button", { "aria-label": ariaToggle === undefined ? translate('local:toggle') : ariaToggle, className: buildClassName('pointer-events-none flex h-6 min-w-6 basis-6 items-center justify-center border-none bg-transparent p-0 focus-visible:outline-none'), ref: toggleButtonRef, ...pressProps, children: _jsx(GeneralIcons.CarretIcon, { className: buildClassName('transition-transform duration-200', directionToTailwindMap[chevronDirection]), size: "24px" }) }), _jsx("div", { className: "pointer-events-none flex h-6 w-full items-center overflow-hidden", children: _jsx("p", { className: buildClassName('block w-[94%] overflow-hidden text-ellipsis whitespace-nowrap', 'skyline-text-subtitle-2 m-0 text-left text-black'), children: title }) }), asideContent && (_jsx("aside", { className: buildClassName('invisible relative z-10 h-6', {
                            visible: isHovered || isSectionFocused || isSelected,
                        }), children: asideContent })), actionMenuConfig && actionMenu && (_jsx(IconButton, { "aria-label": actionMenuConfig.ariaLabel === undefined
                            ? translate('local:options')
                            : actionMenuConfig.ariaLabel, className: "focus-visible:ring-primary-blue group absolute right-6 top-2.5 h-6 focus-visible:outline-none focus-visible:ring-2", ref: actionMenuConfig.triggerRef, variant: "text", ...actionMenuConfig.triggerProps, children: _jsx(GeneralIcons.OptionsIcon, { size: "24px", className: buildClassName('invisible group-focus-visible:visible', {
                                visible: isHovered || isSectionFocused || isSelected,
                            }) }) })), actionMenuConfig?.isOpen && cloneElement(actionMenu, { placement: 'bottom right' })] }), _jsx(AnimatePresence, { children: isExpanded && (_jsx(motion.section, { id: uniqueId, initial: "invisible", animate: "visible", exit: "exit", transition: { type: 'spring', duration: 0.3 }, variants: {
                        invisible: {
                            opacity: 0,
                            y: '-40%',
                            height: 0,
                        },
                        visible: {
                            opacity: 1,
                            y: 0,
                            height: 'auto',
                            overflow: 'hidden',
                        },
                        exit: {
                            opacity: 0,
                            height: 0,
                        },
                    }, children: children })) })] }));
};
export default ToggleSection;
//# sourceMappingURL=ToggleSection.js.map